import { useEffect, useRef } from 'react';
import { Star } from 'lucide-react';
import {
  isOnScreen,
  isTypingTarget,
} from '@/components/features/mindspace/practice/runners/isOnScreen';

interface StarButtonProps {
  /** Whether the concept is starred. Controlled: the host owns the state
   *  (`useConceptStars`) so every star shown for one concept agrees. */
  starred: boolean;
  /** Flip the star. The host performs the write. */
  onToggle: () => void;
  /** Bind `s` to toggle the star while the runner is focused. The host
   *  decides whether to expose the shortcut (e.g. flashcard yes, quiz
   *  yes; setup form no). */
  enableShortcut?: boolean;
}

/**
 * Per-concept star/bookmark toggle — presentation and the `s` shortcut only.
 *
 * It used to keep its own local starred mirror. The flashcard renders one of
 * these per card face, so two mirrors disagreed: star the question, flip,
 * and the answer's star was empty. The state now lives with the host.
 */
export function StarButton({
  starred,
  onToggle,
  enableShortcut = true,
}: StarButtonProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!enableShortcut) return;
    const onKey = (e: KeyboardEvent) => {
      // Visible copy only. The layout mounts the runner twice, so an ungated
      // `s` fired the toggle once per copy — an even number of which is a
      // silent no-op, the worst way for a shortcut to fail. See `isOnScreen`.
      // `isOnScreen` cannot tell a card face turned away by a 3D rotation
      // from the one facing the user, so a host that renders one star per
      // face must enable the shortcut on the shown face only (FlashcardRunner
      // does).
      if (!isOnScreen(buttonRef.current)) return;
      if (isTypingTarget(e)) return;
      if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        onToggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enableShortcut, onToggle]);

  const tone = starred
    ? 'border-brand-300 bg-brand-50 text-brand-600 dark:border-primary dark:tint-brand'
    : 'border-border text-muted-foreground hover:border-brand-200 hover:text-brand-500 dark:hover:border-primary dark:hover:text-primary';

  return (
    <button
      ref={buttonRef}
      type='button'
      onClick={onToggle}
      aria-pressed={starred}
      aria-label={starred ? 'Unstar concept' : 'Star concept for later'}
      title={starred ? 'Starred - click to remove' : 'Star this concept (S)'}
      className={`tap-target inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors max-md:h-11 max-md:w-11 max-md:rounded-xl ${tone}`}
    >
      <Star
        className={`h-3.5 w-3.5 motion-safe:transition-all ${starred ? 'fill-current' : 'fill-none'}`}
      />
    </button>
  );
}
